import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../../admin/api";

export default function MenuImagesPublic() {
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith("de") ? "de" : "en";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // Modal states
  const [openGroup, setOpenGroup] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setErr("");

    api
      .get("/api/menu-images", { params: { lang } })
      .then((res) => {
        if (mounted) {
          const sorted = Array.isArray(res.data)
            ? res.data.sort(
                (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
              )
            : [];
          setRows(sorted);
        }
      })
      .catch((e) =>
        setErr(e?.response?.data?.error || "Failed to load images")
      )
      .finally(() => setLoading(false));

    return () => {
      mounted = false;
    };
  }, [lang]);

  // ✅ Group all images by title
  const grouped = rows.reduce((acc, img) => {
    if (!acc[img.title]) acc[img.title] = [];
    acc[img.title].push(img);
    return acc;
  }, {});

  // Modal navigation
  const nextImage = () => {
    if (!openGroup) return;
    setCurrentIndex((i) => (i + 1) % openGroup.length);
  };

  const prevImage = () => {
    if (!openGroup) return;
    setCurrentIndex((i) => (i - 1 + openGroup.length) % openGroup.length);
  };

  return (
    <section className="scroll-mt-20 pt-4 sm:pt-6 pb-12 sm:pb-16 bg-gray-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
        <h2 className="font-display text-2xl sm:text-3xl md:text-4xl text-gray-900 mb-4 sm:mb-6 text-center">
          {lang === "de" ? "Mittagsrabatt Bilder" : "Midday Discount Images"}
        </h2>

        {loading ? (
          <p className="text-gray-600 text-center py-4">Loading…</p>
        ) : err ? (
          <p className="text-red-600 text-center py-4">{err}</p>
        ) : Object.keys(grouped).length === 0 ? (
          <p className="text-gray-600 text-center py-4">
            {lang === "de" ? "Keine Bilder verfügbar." : "No images available."}
          </p>
        ) : (
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {Object.entries(grouped).map(([title, imgs]) => (
              <li
                key={title}
                className="flex flex-col items-center p-4 border rounded-lg bg-white shadow-sm hover:shadow-md transition"
              >
                <div className="font-medium text-gray-900 text-lg mb-3 text-center">
                  {title}
                </div>

                {/* Preview of first image */}
                <img
                  src={imgs[0].url}
                  alt={title}
                  className="h-40 w-full object-cover rounded-md shadow-sm cursor-pointer hover:scale-105 transition-transform"
                  onClick={() => {
                    setOpenGroup(imgs);
                    setCurrentIndex(0);
                  }}
                />
                <div className="text-xs text-gray-500 mt-2">
                  {(imgs[0]?.language || "en").toUpperCase()}
                </div>
                <button
                  onClick={() => {
                    setOpenGroup(imgs);
                    setCurrentIndex(0);
                  }}
                  className="mt-3 px-4 py-2 rounded-md bg-yellow-500 hover:bg-yellow-600 text-white text-sm font-medium transition"
                >
                  {lang === "de" ? "Ansehen" : "View All"}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* --- Modal Viewer --- */}
      {openGroup && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <button
            className="absolute top-4 right-4 text-white text-3xl font-bold"
            onClick={() => setOpenGroup(null)}
          >
            ×
          </button>

          <div className="relative w-full max-w-3xl flex items-center justify-center">
            {/* Left Arrow */}
            {openGroup.length > 1 && (
              <button
                onClick={prevImage}
                className="absolute left-4 text-white bg-black/50 hover:bg-black/70 rounded-full p-2"
              >
                ‹
              </button>
            )}

            {/* Image */}
            <img
              src={openGroup[currentIndex].url}
              alt="menu"
              className="max-h-[80vh] w-auto rounded-lg shadow-lg"
            />

            {/* Right Arrow */}
            {openGroup.length > 1 && (
              <button
                onClick={nextImage}
                className="absolute right-4 text-white bg-black/50 hover:bg-black/70 rounded-full p-2"
              >
                ›
              </button>
            )}
          </div>

          {/* Counter */}
          <div className="absolute bottom-6 text-gray-200 text-sm">
            {currentIndex + 1} / {openGroup.length}
          </div>
        </div>
      )}
    </section>
  );
}
